import CurrentUser from '../../services/CurrentUser.js';
import api from '../../Api.js';
import {
  PRODUCT_PROFILE,
  PROFILE_ENABLED_CAPABILITIES,
  CAPABILITY,
} from './productProfiles';

/** Default company row from the profile stored on CurrentUser. */
export const getDefaultUserCompany = () => {
  const currentUser = CurrentUser.getUser();
  if (!currentUser?.user_companies?.length) return null;
  return (
    currentUser.user_companies.find(c => c.default === true) ||
    currentUser.user_companies[0]
  );
};

/**
 * Product package for the current default company.
 * Accepts either nested profile `company.product_profile` or a flat client
 * object (`product_profile`) used by the header company switcher.
 */
export const getProductProfileFromCompanyRow = companyRow => {
  if (!companyRow) return PRODUCT_PROFILE.STANDARD;
  return (
    companyRow.company?.product_profile ||
    companyRow.product_profile ||
    PRODUCT_PROFILE.STANDARD
  );
};

export const getCurrentProductProfile = () =>
  getProductProfileFromCompanyRow(getDefaultUserCompany());

/**
 * True if the current company package includes this capability code
 * (e.g. `insights.data_quality`).
 *
 * Today: derived from `product_profile` allow-list.
 * Later: swap body to `CurrentUser` company capability set from the API —
 * keep the same capability codes at call sites.
 */
export const isCapabilityEnabledForCurrentCompany = capabilityCode => {
  const profile = getCurrentProductProfile();
  const enabled = PROFILE_ENABLED_CAPABILITIES[profile];
  if (!enabled) return false;
  return enabled.includes(capabilityCode);
};

/** True if any of the capability codes is enabled for the current company. */
export const isAnyCapabilityEnabledForCurrentCompany = capabilityCodes =>
  Array.isArray(capabilityCodes) &&
  capabilityCodes.some(code => isCapabilityEnabledForCurrentCompany(code));

export const getLandingPath = userData => {
  if (!userData?.user_companies?.length) return '/homePage';
  const defaultCompany =
    userData.user_companies.find(c => c.default) || userData.user_companies[0];
  const profile = getProductProfileFromCompanyRow(defaultCompany);
  const enabled = PROFILE_ENABLED_CAPABILITIES[profile] || [];
  if (enabled.includes(CAPABILITY.DQH_HOME)) {
    return '/dqh/batch-processes';
  }
  return '/homePage';
};

export const hasAccessPermissions = userRoles => {
  let currentUser = CurrentUser.getUser();
  let isAuthorized = false;
  if (currentUser) {
    if (userRoles.includes('*')) return true;
    let currentCompany = currentUser?.user_companies.filter(
      company => company?.default === true
    );
    isAuthorized =
      userRoles.some(role => currentCompany[0]?.roles?.includes(role)) || false;
  }
  return isAuthorized;
};

export const updateCurrentUser = async setUser => {
  const updatedProfile = await api.getUserProfile();
  CurrentUser.setUser(updatedProfile.data);
  setUser({
    user_id: updatedProfile.data.user_id,
    first_name: updatedProfile.data.firstname,
    last_name: updatedProfile.data.lastname,
    email: updatedProfile.data.email,
    phone_number: updatedProfile.data.phone_number,
    title: updatedProfile.data.title,
    clients: updatedProfile.data.user_companies.map(company => {
      return {
        company: company.company.company_name,
        company_id: company.company.company_id,
        default: company?.default,
        roles: company?.roles,
        product_profile:
          company.company?.product_profile || PRODUCT_PROFILE.STANDARD,
      };
    }),
  });
};

export const errorHandler = (
  error,
  setValidationErrors,
  setOpenDialog,
  setToastNotificationConfig
) => {
  if (error?.response?.status === 400) {
    const messageInfo = error.response.data;
    setValidationErrors(messageInfo);
    setOpenDialog(true);
    return { valid: false, error }; // Return failure status
  } else {
    setToastNotificationConfig({
      open: true,
      kind: 'error',
      title: 'Error Saving Configuration!',
      subtitle: 'Please try again later',
    });
    return { valid: false, error };
  }
};

export const smoothScrollByID = id => {
  const element = document.getElementById(id);
  element && element.scrollIntoView({ behavior: 'smooth' });
  return;
};
